import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../../components/layout/AppNav';
import { Badge } from '../../components/ui/Badge';
import { reportsService } from '../../services/reports.service';
import { usersService, type UserProfile } from '../../services/users.service';
import { productsService } from '../../services/products.service';
import { type Offer } from '../../services/offers.service';
import { formatSAR, formatDate } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import {
  Download, TrendingUp, TrendingDown, Send, CheckCircle2, Clock,
  Activity, FileText, Search, Users, CalendarDays,
} from 'lucide-react';

type Period = 'month' | 'quarter' | 'year' | 'custom';

function periodDates(p: Period, customFrom?: string, customTo?: string): { from: string; to: string; label: string } {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  if (p === 'custom') {
    return {
      from: customFrom ?? today,
      to: customTo ?? today,
      label: customFrom && customTo ? `${customFrom} – ${customTo}` : 'Custom',
    };
  }
  if (p === 'month') {
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
      to: today,
      label: now.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    };
  }
  if (p === 'quarter') {
    const q = Math.floor(now.getMonth() / 3);
    return {
      from: new Date(now.getFullYear(), q * 3, 1).toISOString().slice(0, 10),
      to: today,
      label: `Q${q + 1} ${now.getFullYear()}`,
    };
  }
  return {
    from: new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10),
    to: today,
    label: String(now.getFullYear()),
  };
}

function groupByMonth(offers: Offer[]): { month: string; revenue: number; count: number }[] {
  const map: Record<string, { revenue: number; count: number }> = {};
  offers.forEach((o) => {
    if (!o.createdAt) return;
    const key = new Date(o.createdAt).toLocaleString('en-US', { month: 'short', year: '2-digit' });
    if (!map[key]) map[key] = { revenue: 0, count: 0 };
    map[key].revenue += Number(o.total) || 0;
    map[key].count++;
  });
  return Object.entries(map).map(([month, v]) => ({ month, ...v })).slice(-7);
}

export const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const isResellerAdmin = user?.role === 'reseller-admin';

  const [activePeriod, setActivePeriod] = useState<Period>('quarter');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [data, setData] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [chartMode, setChartMode] = useState<'revenue' | 'count'>('revenue');
  const [agents, setAgents] = useState<UserProfile[]>([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedCloseReason, setSelectedCloseReason] = useState('');
  const [productMap, setProductMap] = useState<Record<string, string>>({});

  const load = useCallback((p: Period, cf?: string, ct?: string, agentId?: string, closeReason?: string) => {
    const { from, to } = periodDates(p, cf, ct);
    if (p === 'custom' && (!cf || !ct)) return;
    setLoading(true);
    reportsService.list({ from, to, agentId: agentId || undefined, closeReason: closeReason || undefined })
      .then(setData)
      .catch(() => showToast('Failed to load report', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => { load('quarter'); }, [load]);

  useEffect(() => {
    if (isResellerAdmin) {
      usersService.list().then(setAgents).catch(() => {});
    }
    productsService.list().then((products) => {
      const map: Record<string, string> = {};
      products.forEach((p) => { map[p.id] = p.name; });
      setProductMap(map);
    }).catch(() => {});
  }, [isResellerAdmin]);

  const handlePeriod = (p: Period) => {
    setActivePeriod(p);
    if (p !== 'custom') load(p, undefined, undefined, selectedAgent, selectedCloseReason);
  };

  const handleApplyCustom = () => {
    if (!customFrom || !customTo) { showToast('Please select both From and To dates', 'error'); return; }
    load('custom', customFrom, customTo, selectedAgent, selectedCloseReason);
  };

  const handleAgentChange = (agentId: string) => {
    setSelectedAgent(agentId);
    load(activePeriod, customFrom, customTo, agentId, selectedCloseReason);
  };

  const handleCloseReasonChange = (r: string) => {
    setSelectedCloseReason(r);
    load(activePeriod, customFrom, customTo, selectedAgent, r);
  };

  const handleExport = () => {
    const { from, to } = periodDates(activePeriod, customFrom, customTo);
    setExporting(true);
    reportsService.exportCsv({ from, to, agentId: selectedAgent || undefined, closeReason: selectedCloseReason || undefined })
      .catch(() => showToast('Export failed', 'error'))
      .finally(() => setExporting(false));
  };

  // Computed metrics
  const accepted = data.filter((o) => o.status === 'Accepted');
  const sent = data.filter((o) => ['Sent', 'Accepted', 'Rejected', 'Closed', 'Expired'].includes(o.status));
  const active = data.filter((o) => o.status === 'Sent');
  const rejected = data.filter((o) => o.status === 'Rejected');
  const expired = data.filter((o) => o.status === 'Expired' || (o.status === 'Sent' && o.customerLinkExpiresAt && new Date(o.customerLinkExpiresAt) < new Date()));
  const totalRevenue = accepted.reduce((s, o) => s + (Number(o.total) || 0), 0);
  const convRate = sent.length > 0 ? Math.round((accepted.length / sent.length) * 100) : 0;
  const avgDaysToAccept = (() => {
    const times = accepted.filter((o) => o.sentAt && o.updatedAt)
      .map((o) => (new Date(o.updatedAt).getTime() - new Date(o.sentAt!).getTime()) / 86400000);
    return times.length ? (times.reduce((a, b) => a + b, 0) / times.length).toFixed(1) : null;
  })();
  const { label: periodLabel } = periodDates(activePeriod, customFrom, customTo);

  // Top Products from accepted offers
  const topProducts = (() => {
    const counts: Record<string, { name: string; count: number; value: number }> = {};
    accepted.forEach((o) => {
      ((o as any).items ?? []).forEach((item: any) => {
        const name = productMap[item.productId] ?? item.product?.name ?? 'Unknown';
        if (!counts[name]) counts[name] = { name, count: 0, value: 0 };
        counts[name].count++;
        counts[name].value += Number(item.price) || 0;
      });
    });
    return Object.values(counts).sort((a, b) => b.value - a.value).slice(0, 5);
  })();

  const kpiTiles = [
    { label: 'TOTAL REVENUE', value: formatSAR(totalRevenue), sub: `From accepted offers · ${periodLabel}`, color: 'var(--p)', icon: <TrendingUp size={18} /> },
    { label: 'OFFERS SENT', value: String(sent.length), sub: `Sent this ${activePeriod}`, color: '#3B82F6', icon: <Send size={18} /> },
    { label: 'CONVERSION RATE', value: `${convRate}%`, sub: 'Sent → Accepted', color: convRate >= 50 ? '#3B82F6' : convRate >= 25 ? 'var(--warn)' : 'var(--err)', icon: <Activity size={18} /> },
    { label: 'AVG. TIME TO ACCEPT', value: avgDaysToAccept ? `${avgDaysToAccept}d` : '—', sub: 'Average days from sent', color: 'var(--text-1)', icon: <Clock size={18} /> },
    { label: 'ACTIVE PIPELINE', value: String(active.length), sub: 'Sent offers awaiting response', color: '#F59E0B', icon: <Send size={18} /> },
    { label: 'ACCEPTED OFFERS', value: String(accepted.length), sub: `This ${activePeriod}`, color: 'var(--succ)', icon: <CheckCircle2 size={18} /> },
    { label: 'REJECTED / EXPIRED', value: String(rejected.length + expired.length), sub: `${rejected.length} rejected · ${expired.length} expired`, color: 'var(--err)', icon: <TrendingDown size={18} /> },
    { label: 'MONTHLY RECURRING', value: formatSAR(totalRevenue), sub: 'From all active contracts', color: 'var(--p)', icon: <TrendingUp size={18} /> },
  ];

  const monthly = groupByMonth(data);
  const maxChartVal = Math.max(...monthly.map((m) => chartMode === 'revenue' ? m.revenue : m.count), 1);
  const maxProductVal = topProducts[0]?.value || 1;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Reports" subtitle={`Sales performance · ${periodLabel}`} />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>

        {/* Header */}
        <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div className="page-title">Reports</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 2 }}>
              {loading ? 'Loading…' : `${data.length} offers · ${periodLabel}`}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Agent filter — reseller-admin only */}
            {isResellerAdmin && agents.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Users size={14} color="var(--text-3)" />
                <select
                  className="inp"
                  value={selectedAgent}
                  onChange={(e) => handleAgentChange(e.target.value)}
                  style={{ fontSize: 13, padding: '6px 10px', minWidth: 140 }}
                >
                  <option value="">All Agents</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.keycloakId}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Close Reason filter */}
            <select
              className="inp"
              value={selectedCloseReason}
              onChange={(e) => handleCloseReasonChange(e.target.value)}
              style={{ fontSize: 13, padding: '6px 10px', minWidth: 140 }}
            >
              <option value="">All Reasons</option>
              <option value="Success">Success</option>
              <option value="Rejected">Rejected</option>
              <option value="Lost">Lost</option>
            </select>

            {/* Period toggle */}
            <div style={{ display: 'flex', background: 'var(--surface-alt)', borderRadius: 8, padding: 3, gap: 2 }}>
              {(['month', 'quarter', 'year', 'custom'] as Period[]).map((p) => (
                <button key={p} onClick={() => handlePeriod(p)} style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 13,
                  fontWeight: activePeriod === p ? 600 : 500,
                  cursor: 'pointer', border: 'none', fontFamily: 'inherit',
                  background: activePeriod === p ? 'var(--surface)' : 'transparent',
                  color: activePeriod === p ? 'var(--text-1)' : 'var(--text-2)',
                  boxShadow: activePeriod === p ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                  transition: 'all .15s',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  {p === 'custom' && <CalendarDays size={12} />}
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>

            <button className="btn-ghost btn-sm" onClick={handleExport} disabled={data.length === 0 || exporting}
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Download size={14} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>

        {/* Custom date range pickers */}
        {activePeriod === 'custom' && (
          <div className="card" style={{ padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <CalendarDays size={15} color="var(--p)" />
            <label className="lbl" style={{ marginBottom: 0 }}>From</label>
            <input type="date" className="inp" value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              style={{ fontSize: 13, padding: '6px 10px' }} />
            <label className="lbl" style={{ marginBottom: 0 }}>To</label>
            <input type="date" className="inp" value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              style={{ fontSize: 13, padding: '6px 10px' }} />
            <button className="btn-primary" onClick={handleApplyCustom}
              style={{ padding: '7px 18px', fontSize: 13 }}>
              Apply
            </button>
            {customFrom && customTo && (
              <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                {customFrom} – {customTo}
              </span>
            )}
          </div>
        )}

        {/* 8 KPI tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          {kpiTiles.map((tile) => (
            <div key={tile.label} className="card" style={{ padding: '18px 20px' }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                {tile.label}
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: tile.color, lineHeight: 1, marginBottom: 4 }}>
                {loading ? <div className="skeleton" style={{ height: 26, width: 80 }} /> : tile.value}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{tile.sub}</div>
            </div>
          ))}
        </div>

        {/* Monthly Revenue Trend */}
        <div className="card">
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="section-title" style={{ marginBottom: 0 }}>Monthly Revenue Trend (SAR)</span>
            <div style={{ display: 'flex', background: 'var(--surface-alt)', borderRadius: 6, padding: 2 }}>
              {(['revenue', 'count'] as const).map((m) => (
                <button key={m} onClick={() => setChartMode(m)} style={{
                  padding: '4px 12px', borderRadius: 4, fontSize: 12.5, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: chartMode === m ? 'var(--surface)' : 'transparent',
                  color: chartMode === m ? 'var(--text-1)' : 'var(--text-2)',
                  fontWeight: chartMode === m ? 600 : 400,
                  boxShadow: chartMode === m ? '0 1px 3px rgba(0,0,0,.07)' : 'none',
                  transition: 'all .15s',
                }}>
                  {m === 'revenue' ? 'Revenue' : 'Offer Count'}
                </button>
              ))}
            </div>
          </div>
          <div style={{ padding: '28px 24px 20px' }}>
            {loading ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="skeleton" style={{ flex: 1, height: `${40 + i * 8}%`, borderRadius: 6 }} />
                ))}
              </div>
            ) : monthly.length === 0 ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 14 }}>
                No data for this period.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 240 }}>
                  {monthly.map((m, i) => {
                    const val = chartMode === 'revenue' ? m.revenue : m.count;
                    const pct = (val / maxChartVal) * 100;
                    const isLast = i === monthly.length - 1;
                    return (
                      <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 4, fontWeight: 500 }}>
                          {chartMode === 'revenue'
                            ? (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : String(Math.round(val)))
                            : String(val)}
                        </div>
                        <div style={{
                          width: '100%', borderRadius: '5px 5px 0 0',
                          background: isLast ? 'var(--p)' : 'rgba(0,56,49,.18)',
                          height: `${Math.max(pct, 2)}%`,
                          transition: 'height .4s ease',
                        }} title={`${m.month}: ${chartMode === 'revenue' ? formatSAR(val) : `${val} offers`}`} />
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                  {monthly.map((m) => (
                    <div key={m.month} style={{ flex: 1, textAlign: 'center', fontSize: 12, color: 'var(--text-2)' }}>{m.month}</div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Top Products + Conversion Summary */}
        {!loading && data.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>

            {/* Top Products (Accepted only) */}
            <div className="card">
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <TrendingUp size={15} color="var(--p)" />
                <span className="section-title" style={{ marginBottom: 0 }}>Top Products (Accepted)</span>
              </div>
              <div className="card-body" style={{ padding: '16px 20px' }}>
                {topProducts.length === 0 ? (
                  <div style={{ color: 'var(--text-3)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                    No accepted offers in this period.
                  </div>
                ) : topProducts.map((p, i) => (
                  <div key={p.name} style={{ marginBottom: i < topProducts.length - 1 ? 16 : 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)' }}>{p.name}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{formatSAR(p.value)}</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: 'var(--border)' }}>
                      <div style={{ height: '100%', borderRadius: 3, background: 'var(--p)', width: `${(p.value / maxProductVal) * 100}%`, transition: 'width .5s ease' }} />
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3 }}>{p.count} accepted offer{p.count !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Conversion Summary */}
            <div className="card">
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle2 size={15} color="var(--succ)" />
                <span className="section-title" style={{ marginBottom: 0 }}>Conversion Summary</span>
              </div>
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    { label: 'CONVERSION RATE', value: `${convRate}%`, sub: 'Sent → Accepted', color: convRate >= 50 ? '#3B82F6' : convRate >= 25 ? 'var(--warn)' : 'var(--err)' },
                    { label: 'PERIOD REVENUE', value: formatSAR(totalRevenue), sub: 'Accepted offers', color: 'var(--p)' },
                    { label: 'AVG. TIME TO ACCEPT', value: avgDaysToAccept ? `${avgDaysToAccept}d` : '—', sub: 'Days from sent', color: 'var(--text-1)' },
                    { label: 'ACTIVE OFFERS', value: String(active.length), sub: 'Awaiting response', color: '#3B82F6' },
                  ].map((b) => (
                    <div key={b.label} style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{b.label}</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: b.color, lineHeight: 1 }}>{b.value}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{b.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Offers table */}
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={15} color="var(--p)" />
              <span className="section-title" style={{ marginBottom: 0 }}>Offers in this period</span>
              {data.length > 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-3)', background: 'var(--surface-alt)', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                  {data.length}
                </span>
              )}
            </div>
            {data.length > 0 && (
              <div style={{ position: 'relative', maxWidth: 260 }}>
                <Search size={14} color="var(--text-3)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
                <input type="search" placeholder="Search offer #, customer…"
                  value={tableSearch} onChange={(e) => setTableSearch(e.target.value)}
                  className="inp" style={{ paddingLeft: 32, fontSize: 13 }} />
              </div>
            )}
          </div>
          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <div className="skeleton" style={{ height: 14, width: 200, margin: '0 auto' }} />
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><FileText size={22} /></div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>No offers found</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Select a period or apply filters to see offers.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Offer #</th><th>Customer</th><th>Status</th>
                    <th>Close Reason</th><th>Total</th><th>Created By</th><th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data
                    .filter((o) => {
                      if (!tableSearch) return true;
                      const q = tableSearch.toLowerCase();
                      return (
                        o.offerNumber?.toLowerCase().includes(q) ||
                        (o.customer?.nameEn ?? '').toLowerCase().includes(q) ||
                        (o.customer?.crNumber ?? '').toLowerCase().includes(q)
                      );
                    })
                    .map((offer) => (
                      <tr key={offer.id} onClick={() => navigate(`/my-offers/${offer.id}`)} style={{ cursor: 'pointer' }}>
                        <td style={{ fontWeight: 700, color: 'var(--p)' }}>{offer.offerNumber}</td>
                        <td>{offer.customer?.nameEn ?? <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                        <td><Badge label={offer.status} /></td>
                        <td style={{ fontSize: 12 }}>
                          {offer.status === 'Closed' && offer.closeReason
                            ? <span style={{
                                padding: '2px 8px', borderRadius: 12, fontWeight: 600,
                                background: offer.closeReason === 'Success' ? '#d1fae5' : offer.closeReason === 'Rejected' ? '#fee2e2' : '#fef3c7',
                                color: offer.closeReason === 'Success' ? '#065f46' : offer.closeReason === 'Rejected' ? '#991b1b' : '#92400e',
                              }}>{offer.closeReason}</span>
                            : <span style={{ color: 'var(--text-3)' }}>—</span>}
                        </td>
                        <td style={{ fontWeight: 600 }}>{formatSAR(offer.total)}</td>
                        <td style={{ color: 'var(--text-2)', fontSize: 13 }}>
                          {offer.agentName ?? <span style={{ color: 'var(--text-3)' }}>—</span>}
                        </td>
                        <td style={{ color: 'var(--text-2)', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {formatDate(offer.createdAt)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
